'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'

import {
  DataDisplayType,
  LOADING_ANIMATION_TIMING,
} from '@/shared/constants/animations'
import {
  formatLoadingValue,
  generateRandomValue,
  getDataTypeConfig,
} from '@/shared/helpers/loading/stat-cards-and-charts'
import { cn } from '@/utils/shadcn/utils'

interface DigitByDigitAnimationProps {
  className?: string
  targetValue?: number
  minValue?: number
  maxValue?: number
  duration?: number
  digitChangeInterval?: number
  formatValue?: (value: number) => string
  type?: DataDisplayType
  decimalPlaces?: number
  prefix?: string
  suffix?: string
}

export function DigitByDigitAnimation({
  className = '',
  targetValue,
  minValue,
  maxValue,
  duration = LOADING_ANIMATION_TIMING.DIGIT_ANIMATION_DURATION,
  digitChangeInterval = LOADING_ANIMATION_TIMING.DIGIT_CHANGE_INTERVAL,
  formatValue,
  type = DataDisplayType.INTEGER,
  decimalPlaces = 2,
  prefix = '',
  suffix = '',
}: DigitByDigitAnimationProps) {
  // Get configuration for the data type
  const typeConfig = getDataTypeConfig(type, {
    minValue,
    maxValue,
    decimalPlaces,
  })
  const finalMinValue = minValue ?? typeConfig.minValue
  const finalMaxValue = maxValue ?? typeConfig.maxValue

  // Initialize with safe default values for SSR
  const [currentValue, setCurrentValue] = useState(finalMinValue)
  const [displayValue, setDisplayValue] = useState(finalMinValue)
  const [isClient, setIsClient] = useState(false)

  const formatValueFunction = (value: number) => {
    // Guard against undefined values
    // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
    if (value === undefined || value === null || isNaN(value)) {
      // biome-ignore lint/style/useTemplate: Bulk suppression as part of biome migration
      return prefix + '0' + suffix
    }

    if (formatValue) return formatValue(value)

    return formatLoadingValue({
      type,
      value,
      decimalPlaces,
      prefix,
      suffix,
    })
  }

  // Set client flag after mount to prevent hydration issues
  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    // Only run animations on client side
    if (!isClient) return

    if (targetValue !== undefined) {
      // Animate to target value
      const startValue = currentValue
      const endValue = targetValue
      const steps = Math.ceil(duration / digitChangeInterval)
      let currentStep = 0

      const interval = setInterval(() => {
        currentStep++
        const progress = currentStep / steps
        const newValue = startValue + (endValue - startValue) * progress

        setCurrentValue(newValue)

        if (currentStep >= steps) {
          setCurrentValue(endValue)
          clearInterval(interval)
        }
      }, digitChangeInterval)

      return () => clearInterval(interval)
    } else {
      // Random animation mode - only on client
      const interval = setInterval(() => {
        const randomValue = generateRandomValue(
          type,
          finalMinValue,
          finalMaxValue,
        )
        setCurrentValue(randomValue)
      }, digitChangeInterval)

      return () => clearInterval(interval)
    }
  }, [
    targetValue,
    finalMinValue,
    finalMaxValue,
    duration,
    digitChangeInterval,
    type,
    currentValue,
    isClient,
  ])

  useEffect(() => {
    setDisplayValue(currentValue)
  }, [currentValue])

  const formattedValue = formatValueFunction(displayValue)

  // Split into numbers and text parts
  const parts = formattedValue.split(/(\d+)/).filter((part) => part.length > 0)

  if (!displayValue) {
    // add a placeholder square when displayValue is undefined
    return <div className="w-1.5 h-[20px]"></div>
  }

  return (
    <div className={cn('relative overflow-hidden', className)}>
      <div className="relative">
        <AnimatePresence mode="wait">
          <div
            key={formattedValue}
            className="flex flex-row items-center gap-[1.5px]"
          >
            {/* Render all parts in order */}
            {parts.map((part, index) => {
              if (/^\d+$/.test(part)) {
                // Numeric part - render as squares
                return part.split('').map((digit, digitIndex) => (
                  <motion.div
                    key={`num-${index}-${digitIndex}-${digit}-${formattedValue}`}
                    className="w-1.5 h-3 bg-moment-blue-grey-200 dark:bg-moment-grey-700/80 rounded-[1px]"
                    initial={{ y: 20, opacity: 0, scale: 0.8 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: -20, opacity: 0, scale: 0.8 }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.05 + digitIndex * 0.05,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                  />
                ))
              } else {
                // Text part - render as static text
                return (
                  <span
                    key={`text-${index}-${part}-${formattedValue}`}
                    className={cn(
                      'text-moment-blue-grey-400 dark:text-moment-grey-500/80 text-xs ml-0.5',
                      part === '.' && '!ml-0',
                    )}
                  >
                    {part}
                  </span>
                )
              }
            })}
          </div>
        </AnimatePresence>
      </div>
    </div>
  )
}
